import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { ATLASSIAN_SUPPORT_URL } from '@confluence/error-boundary';

export enum ErrorCodes {
	VALIDATION_ERROR = 400,
	AUTHORIZATION_ERROR = 401,
	NOT_FOUND_ERROR = 404,
	CONTENT_ERROR = 500,
}

export const i18n = defineMessages({
	validationErrorTitle: {
		id: 'template-utils.errors.validation.template.select.title',
		defaultMessage: 'We couldn’t load the template',
		description: 'Error title for validation template creation error',
	},
	validationErrorDescription: {
		id: 'template-utils.errors.validation.template.select.description',
		defaultMessage: 'Try a different template, or create a new page and select the template again.',
		description: 'Error description for validation template creation error',
	},
	authorizationErrorTitle: {
		id: 'template-utils.errors.authorization.template.select.title',
		defaultMessage: 'We couldn’t load the template',
		description: 'Error title for authorization template creation error',
	},
	authorizationErrorDescription: {
		id: 'template-utils.errors.authorization.template.select.description',
		defaultMessage: 'Try refreshing the page.',
		description: 'Error description for authorization template creation error',
	},
	contentErrorTitle: {
		id: 'template-utils.errors.content.template.select.title',
		defaultMessage: 'We can’t apply templates to this page',
		description: 'Error title for content template creation error',
	},
	contentErrorDescription: {
		id: 'template-utils.errors.content.template.select.description',
		defaultMessage: 'Try creating a new page and selecting the template again.',
		description: 'Error description for content template creation error',
	},
	thirdPartyBlueprintErrorTitle: {
		id: 'template-utils.errors.third.party.blueprint.template.select.title',
		defaultMessage: 'This template is broken',
		description: 'Error title when attempting to use a third party template which is broken',
	},
	thirdPartyBlueprintErrorDescription: {
		id: 'template-utils.errors.third.party.blueprint.template.select.description',
		defaultMessage:
			'Since the template is owned by a third-party, it has to be fixed by them. Contact your site admin so they can troubleshoot the issue.',
		description: 'Error message when attempting to use a third party template which is broken',
	},
	editedFirstPartyBlueprintErrorTitle: {
		id: 'template-utils.errors.edited.first.party.blueprint.template.select.title',
		defaultMessage: 'This template is broken',
		description:
			'Error title when attempting to use an edited first party template which is broken',
	},
	editedFirstPartyBlueprintErrorDescription: {
		id: 'template-utils.errors.edited.first.party.blueprint.template.select.description',
		defaultMessage: 'It contains elements that are no longer supported in the Confluence editor.',
		description:
			'Error message when attempting to use an edited first party template which occurs when the template has unsupported elements',
	},
	editedFirstPartyBlueprintErrorAction: {
		id: 'template-utils.errors.edited.first.party.blueprint.template.select.action',
		defaultMessage: 'Contact support',
		description:
			'Error action when attempting to use an edited first party template which is broken',
	},
	notFoundErrorTitle: {
		id: 'template-utils.errors.graphql.not.found.description.title',
		description:
			'title for error message when parent page id of page user is trying to create does not exist',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "We couldn't create a page for you",
	},
	notFoundErrorDescription: {
		id: 'template-utils.errors.graphql.not.found.description',
		description:
			'error message when parent page id of page user is trying to create does not exist',
		defaultMessage: 'Refresh the page to try again.',
	},
});

export const getErrorMessage = (
	errorCode: ErrorCodes,
	isThirdPartyBlueprintError: boolean,
	isEditedFirstPartyBlueprintError: boolean,
) => {
	if (errorCode === ErrorCodes.VALIDATION_ERROR) {
		return {
			title: <FormattedMessage {...i18n.validationErrorTitle} />,
			description: <FormattedMessage {...i18n.validationErrorDescription} />,
		};
	}
	if (errorCode === ErrorCodes.NOT_FOUND_ERROR) {
		return {
			title: <FormattedMessage {...i18n.notFoundErrorTitle} />,
			description: <FormattedMessage {...i18n.notFoundErrorDescription} />,
		};
	}
	if (errorCode === ErrorCodes.CONTENT_ERROR && isThirdPartyBlueprintError) {
		return {
			title: <FormattedMessage {...i18n.thirdPartyBlueprintErrorTitle} />,
			description: <FormattedMessage {...i18n.thirdPartyBlueprintErrorDescription} />,
		};
	}
	if (errorCode === ErrorCodes.CONTENT_ERROR && isEditedFirstPartyBlueprintError) {
		return {
			title: <FormattedMessage {...i18n.editedFirstPartyBlueprintErrorTitle} />,
			description: <FormattedMessage {...i18n.editedFirstPartyBlueprintErrorDescription} />,
			actions: [
				{
					content: <FormattedMessage {...i18n.editedFirstPartyBlueprintErrorAction} />,
					href: ATLASSIAN_SUPPORT_URL,
					target: '_blank',
				},
			],
		};
	}
	if (errorCode === ErrorCodes.CONTENT_ERROR) {
		return {
			title: <FormattedMessage {...i18n.contentErrorTitle} />,
			description: <FormattedMessage {...i18n.contentErrorDescription} />,
		};
	}
	return {
		title: <FormattedMessage {...i18n.authorizationErrorTitle} />,
		description: <FormattedMessage {...i18n.authorizationErrorDescription} />,
	};
};
